import { ILogger } from '@tdp/common/telemetry';

import { LocaleStrings, SupportedLocale } from './models';

interface LocaleFetchSuccessResult {
  type: 'success';
  localeStrings: LocaleStrings;
}

interface LocaleFetchNetworkFailureResult {
  type: 'networkError';
  errorCode: number;
  errorMessage: string;
}

type LocaleFetchResult =
  | LocaleFetchSuccessResult
  | LocaleFetchNetworkFailureResult;

export type LocaleStringFetcher = (
  locale: SupportedLocale
) => Promise<LocaleFetchResult>;

export interface ApiLocalizationFetcherDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

/**
 * Implementation of localization fetcher that goes to the remote TDP host (likely cdn or statics, not actual API)
 * to fetch the locale strings.
 */
export class ApiLocalizationFetcher {
  private apiFetch: typeof fetch;
  private logger: ILogger;

  constructor(deps: ApiLocalizationFetcherDeps) {
    this.apiFetch = deps.apiFetch;
    this.logger = deps.logger;
  }

  public async getLocaleStrings(
    locale: SupportedLocale
  ): Promise<LocaleFetchResult> {
    const response = await this.apiFetch(`/lang/${locale}/strings.json`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (response.status < 200 || response.status >= 300) {
      return {
        type: 'networkError',
        errorCode: response.status,
        errorMessage: response.statusText,
      };
    }

    // for now, we assume the response is well a well formed localization map, if that isn't true format.js will fallback to en-us.
    const strings = (await response.json()) as Record<string, string>;
    this.logger.trace(
      'Fetched %d strings for locale %s',
      Object.keys(strings).length,
      locale
    );

    return {
      type: 'success',
      localeStrings: {
        locale,
        strings,
      },
    };
  }
}
